// src/pages/Gallery/Gallery.jsx
import React, { useEffect, useState } from 'react';
import {
  Table, Button, Container, Alert, Pagination, Modal, Form,
  Card, Row, Col, Image, Badge, Spinner, InputGroup
} from 'react-bootstrap';
import {
  FaEdit, FaTrash, FaPlus, FaTh, FaList, FaSearch,
  FaFilter, FaImages, FaArchive, FaEye
} from 'react-icons/fa';
import axios from 'axios';

// Base URL for API and images
const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

// Allowed image types
const ALLOWED_IMAGE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/svg+xml'
];

const Gallery = () => {
  // State management
  const [galleryItems, setGalleryItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showModal, setShowModal] = useState(false);
  const [currentItem, setCurrentItem] = useState({
    id: null,
    event: '',
    category: '',
    photo: null,
    archive: false,
    existingPhotoPath: null
  });
  const [previewImage, setPreviewImage] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'upload_on', direction: 'desc' });
  const [categories, setCategories] = useState([]);
  const [paginationMeta, setPaginationMeta] = useState({
    total: 0,
    page: 1,
    limit: 10,
    total_pages: 1
  });

  // Fetch gallery items when page changes
  useEffect(() => {
    fetchGalleryItems(currentPage);
  }, [currentPage]);

  // Extract categories when items change
  useEffect(() => {
    if (galleryItems.length > 0) {
      extractCategories();
    }
  }, [galleryItems]);

  // Apply filters when items or filter criteria changes
  useEffect(() => {
    applyFilters();
  }, [galleryItems, searchTerm, selectedCategory, selectedStatus, sortConfig]);

  const fetchGalleryItems = async (page = 1) => {
    setIsLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${BASE_URL}/gallery?page=${page}`, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : ''
        }
      });

      console.log('Gallery API Response:', response.data);

      // Handle response structure based on the API response
      let items = [];
      let pagination = {
        total: 0,
        page: 1,
        limit: 10,
        total_pages: 1
      };

      if (response.data && response.data.status === 'success') {
        // Response format: { status: "success", data: [...], pagination: {...} }
        items = response.data.data || [];
        pagination = response.data.pagination || pagination;
      } else if (response.data && response.data.data) {
        items = response.data.data;
        if (response.data.pagination) {
          pagination = response.data.pagination;
        }
      } else if (Array.isArray(response.data)) {
        items = response.data;
      }

      setGalleryItems(items);
      setPaginationMeta(pagination);
      setTotalItems(pagination.total || items.length);
      setTotalPages(pagination.total_pages || 1);
      setItemsPerPage(pagination.limit || 10);
      setCurrentPage(pagination.page || page);

    } catch (err) {
      console.error('Error fetching gallery items:', err);
      setError('Failed to fetch gallery items: ' + (err.message || 'Server error'));
    } finally {
      setIsLoading(false);
    }
  };

  // Extract unique categories from gallery items
  const extractCategories = () => {
    const uniqueCategories = [...new Set(galleryItems.map(item => item.category))];
    setCategories(uniqueCategories.filter(cat => cat).sort());
  };

  // Apply filters and sorting (client-side filtering on current page)
  const applyFilters = () => {
    let filtered = [...galleryItems];

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(item =>
        item.event?.toLowerCase().includes(term) ||
        item.category?.toLowerCase().includes(term)
      );
    }

    // Apply category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(item => item.category === selectedCategory);
    }

    // Apply status filter (archive)
    if (selectedStatus !== 'all') {
      const archiveStatus = selectedStatus === 'archived';
      filtered = filtered.filter(item => item.archive === (archiveStatus ? 1 : 0));
    }

    // Apply sorting
    filtered.sort((a, b) => {
      if (sortConfig.key) {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Handle date fields
        if (sortConfig.key === 'upload_on') {
          aValue = new Date(aValue);
          bValue = new Date(bValue);
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
      }
      return 0;
    });

    setFilteredItems(filtered);
  };

  // Handle sort request
  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Reset all filters
  const resetFilters = () => {
    setSearchTerm('');
    setSelectedCategory('all');
    setSelectedStatus('all');
    setSortConfig({ key: 'upload_on', direction: 'desc' });
  };

  // Get stats
  const getStats = () => {
    const total = totalItems;
    const active = galleryItems.filter(item => item.archive === 0).length;
    const archived = galleryItems.filter(item => item.archive === 1).length;
    const categoriesCount = categories.length;

    return {
      total,
      active,
      archived,
      categories: categoriesCount,
      currentCount: galleryItems.length
    };
  };

  const stats = getStats();

  // Delete gallery item
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this gallery item? This action cannot be undone.')) {
      return;
    }

    setDeletingId(id);
    setError(null);
    setSuccess(null);

    try {
      const token = localStorage.getItem('authToken');
      await axios.delete(`${BASE_URL}/gallery/${id}`, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : ''
        }
      });

      setSuccess('Gallery item deleted successfully');

      // Check if this was the last item on the current page
      if (galleryItems.length === 1 && currentPage > 1) {
        // Go to previous page
        await fetchGalleryItems(currentPage - 1);
      } else {
        // Refresh the current page
        await fetchGalleryItems(currentPage);
      }

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Delete error:', err);
      setError(`Failed to delete item: ${err.response?.data?.message || err.message || 'Server error'}`);
    } finally {
      setDeletingId(null);
    }
  };

  // Create gallery item
  const createGalleryItem = async (formData) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.post(`${BASE_URL}/gallery`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': token ? `Bearer ${token}` : ''
        }
      });
      return response.data;
    } catch (err) {
      throw new Error(err.response?.data?.message || err.message || 'Failed to create');
    }
  };

  // Update gallery item
  const updateGalleryItem = async (id, formData) => {
    try {
      const token = localStorage.getItem('authToken');
      // Use PUT method with FormData
      const response = await axios.put(`${BASE_URL}/gallery/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': token ? `Bearer ${token}` : ''
        }
      });
      return response.data;
    } catch (err) {
      throw new Error(err.response?.data?.message || err.message || 'Failed to update');
    }
  };

  // Validate form
  const validateForm = () => {
    const errors = {};
    if (!currentItem.event?.trim()) errors.event = 'Event name is required';
    if (!currentItem.category?.trim()) errors.category = 'Category is required';
    if (!currentItem.id && !currentItem.photo) errors.photo = 'Photo is required';
    return errors;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setFormErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      const formData = new FormData();
      formData.append('event', currentItem.event);
      formData.append('category', currentItem.category);
      formData.append('archive', currentItem.archive ? '1' : '0');

      if (currentItem.photo) {
        formData.append('photoPath', currentItem.photo);
      }

      if (currentItem.id) {
        // Update existing item
        await updateGalleryItem(currentItem.id, formData);
        setSuccess('Gallery item updated successfully');
      } else {
        // Create new item
        await createGalleryItem(formData);
        setSuccess('Gallery item created successfully');
        setCurrentPage(1);
      }

      // Refresh the list
      await fetchGalleryItems(currentItem.id ? currentPage : 1);
      setShowModal(false);
      resetForm();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Submit error:', err);
      setError(`Operation failed: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setCurrentItem({
      id: null,
      event: '',
      category: '',
      photo: null,
      archive: false,
      existingPhotoPath: null
    });
    setPreviewImage(null);
    setFormErrors({});
  };

  // Handle file input change
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) {
      setCurrentItem(prev => ({ ...prev, photo: null }));
      setPreviewImage(null);
      return;
    }

    // Validate file type
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setFormErrors(prev => ({ ...prev, photo: 'Only image files are allowed (JPG, PNG, GIF, WEBP, SVG)' }));
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setFormErrors(prev => ({ ...prev, photo: 'File size must be less than 5MB' }));
      return;
    }

    setCurrentItem(prev => ({ ...prev, photo: file }));
    setFormErrors(prev => ({ ...prev, photo: undefined }));

    // Create preview
    const reader = new FileReader();
    reader.onload = (event) => setPreviewImage(event.target.result);
    reader.readAsDataURL(file);
  };

  // Handle edit button click
  const handleEdit = (item) => {
    setCurrentItem({
      id: item.id,
      event: item.event,
      category: item.category,
      photo: null,
      archive: item.archive === 1,
      existingPhotoPath: item.photoPath
    });
    // Use the photoUrl from the API if available
    const photoUrl = item.photoUrl || `${BASE_URL}/${item.photoPath}`;
    setPreviewImage(photoUrl);
    setFormErrors({});
    setShowModal(true);
  };

  // Handle create new item
  const handleCreate = () => {
    resetForm();
    setShowModal(true);
  };

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCurrentItem(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error when field is edited
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  // Handle page change
  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Generate pagination items
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    let items = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    // Previous button
    items.push(
      <Pagination.Prev
        key="prev"
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
      />
    );

    // First page
    if (startPage > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
          1
        </Pagination.Item>
      );
      if (startPage > 2) {
        items.push(<Pagination.Ellipsis key="start-ellipsis" />);
      }
    }

    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <Pagination.Item
          key={i}
          active={i === currentPage}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Pagination.Item>
      );
    }

    // Last page
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(<Pagination.Ellipsis key="end-ellipsis" />);
      }
      items.push(
        <Pagination.Item
          key={totalPages}
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </Pagination.Item>
      );
    }

    // Next button
    items.push(
      <Pagination.Next
        key="next"
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      />
    );

    return items;
  };

  // Get image URL
  const getImageUrl = (item) => {
    //if (item.photoUrl) return item.photoUrl;
    if (item.photoPath) return `${BASE_URL}/public/${item.photoPath}`;
    return '';
  };

  return (
    <Container className="py-4">
      {/* Header Section */}
      <Card className="mb-4 border-0 shadow-sm">
        <Card.Body className="p-4">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2 className="mb-1">Photo Gallery Management</h2>
              <p className="text-muted mb-0">
                Manage your photo gallery with event categorization and archive functionality.
              </p>
            </div>
            <div>
              <Button
                variant={viewMode === 'grid' ? 'primary' : 'outline-primary'}
                onClick={() => setViewMode('grid')}
                className="me-2"
                title="Grid View"
              >
                <FaTh />
              </Button>
              <Button
                variant={viewMode === 'table' ? 'primary' : 'outline-primary'}
                onClick={() => setViewMode('table')}
                className="me-2"
                title="Table View"
              >
                <FaList />
              </Button>
              <Button variant="primary" onClick={handleCreate} disabled={isLoading}>
                <FaPlus className="me-2" />
                Add Photo
              </Button>
            </div>
          </div>

          {/* Search and Filter Section */}
          <Row className="g-3 mb-4">
            <Col md={4}>
              <InputGroup>
                <InputGroup.Text>
                  <FaSearch />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search events or categories..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                  }}
                />
                {searchTerm && (
                  <Button
                    variant="outline-secondary"
                    onClick={() => setSearchTerm('')}
                  >
                    Clear
                  </Button>
                )}
              </InputGroup>
            </Col>
            <Col md={3}>
              <Form.Select
                value={selectedCategory}
                onChange={(e) => {
                  setSelectedCategory(e.target.value);
                }}
              >
                <option value="all">All Categories</option>
                {categories.map((category, index) => (
                  <option key={index} value={category}>
                    {category}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Select
                value={selectedStatus}
                onChange={(e) => {
                  setSelectedStatus(e.target.value);
                }}
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="archived">Archived</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Button
                variant="outline-secondary"
                onClick={resetFilters}
                className="w-100"
                title="Reset Filters"
              >
                <FaFilter /> Reset
              </Button>
            </Col>
          </Row>

          {/* Stats Cards */}
          <Row className="mt-4">
            <Col md={2}>
              <Card className="border-0 bg-light">
                <Card.Body className="py-3">
                  <div className="d-flex justify-content-between">
                    <div>
                      <h6 className="text-muted mb-0">Total Photos</h6>
                      <h4 className="mb-0">{stats.total}</h4>
                    </div>
                    <div className="text-primary">
                      <FaImages size={24} />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={2}>
              <Card className="border-0 bg-light">
                <Card.Body className="py-3">
                  <div className="d-flex justify-content-between">
                    <div>
                      <h6 className="text-muted mb-0">Active</h6>
                      <h4 className="mb-0">{stats.active}</h4>
                    </div>
                    <div className="text-success">
                      <FaEye size={24} />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={2}>
              <Card className="border-0 bg-light">
                <Card.Body className="py-3">
                  <div className="d-flex justify-content-between">
                    <div>
                      <h6 className="text-muted mb-0">Archived</h6>
                      <h4 className="mb-0">{stats.archived}</h4>
                    </div>
                    <div className="text-secondary">
                      <FaArchive size={24} />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={2}>
              <Card className="border-0 bg-light">
                <Card.Body className="py-3">
                  <div className="d-flex justify-content-between">
                    <div>
                      <h6 className="text-muted mb-0">Categories</h6>
                      <h4 className="mb-0">{stats.categories}</h4>
                    </div>
                    <div className="text-info">
                      <Badge bg="info">Cat</Badge>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={2}>
              <Card className="border-0 bg-light">
                <Card.Body className="py-3">
                  <div className="d-flex justify-content-between">
                    <div>
                      <h6 className="text-muted mb-0">On Page</h6>
                      <h4 className="mb-0">{stats.currentCount}</h4>
                    </div>
                    <div className="text-dark">
                      <Badge bg="light" text="dark">Now</Badge>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={2}>
              <Card className="border-0 bg-light">
                <Card.Body className="py-3">
                  <div className="d-flex justify-content-between">
                    <div>
                      <h6 className="text-muted mb-0">Page</h6>
                      <h4 className="mb-0">{currentPage} / {totalPages}</h4>
                    </div>
                    <div className="text-secondary">
                      <Badge bg="secondary">Page</Badge>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Alerts */}
      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible className="mb-4">
          <Alert.Heading>Error</Alert.Heading>
          <p>{error}</p>
        </Alert>
      )}
      {success && (
        <Alert variant="success" onClose={() => setSuccess(null)} dismissible className="mb-4">
          <Alert.Heading>Success</Alert.Heading>
          <p>{success}</p>
        </Alert>
      )}

      {/* Gallery Content */}
      <Card className="border-0 shadow-sm">
        <Card.Body className="p-0">
          {isLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3">Loading gallery items...</p>
            </div>
          ) : viewMode === 'grid' ? (
            <>
              <Row xs={1} md={2} lg={3} xl={4} className="g-4 p-4">
                {filteredItems.length > 0 ? (
                  filteredItems.map(item => (
                    <Col key={item.id}>
                      <Card className="h-100">
                        <div style={{ position: 'relative' }}>
                          <Card.Img
                            variant="top"
                            src={getImageUrl(item)}
                            style={{ height: '200px', objectFit: 'cover' }}
                            alt={item.event}
                            onError={(e) => {
                              e.target.src = 'https://via.placeholder.com/300x200?text=No+Image';
                            }}
                          />
                          {item.archive === 1 && (
                            <Badge
                              bg="secondary"
                              style={{ position: 'absolute', top: '10px', right: '10px' }}
                            >
                              Archived
                            </Badge>
                          )}
                        </div>
                        <Card.Body>
                          <Card.Title className="fs-6">{item.event}</Card.Title>
                          <Card.Text>
                            <small className="text-muted">
                              <strong>Category:</strong> {item.category}
                            </small>
                          </Card.Text>
                          <div className="d-flex justify-content-between align-items-center">
                            <Badge bg={item.archive === 1 ? "secondary" : "success"}>
                              {item.archive === 1 ? 'Archived' : 'Active'}
                            </Badge>
                            <div>
                              <Button
                                variant="outline-primary"
                                onClick={() => handleEdit(item)}
                                className="me-2"
                                size="sm"
                                disabled={deletingId !== null}
                                title="Edit"
                              >
                                <FaEdit />
                              </Button>
                              <Button
                                variant="outline-danger"
                                onClick={() => handleDelete(item.id)}
                                size="sm"
                                disabled={deletingId !== null}
                                title="Delete"
                              >
                                {deletingId === item.id ? (
                                  <Spinner animation="border" size="sm" />
                                ) : (
                                  <FaTrash />
                                )}
                              </Button>
                            </div>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))
                ) : (
                  <Col className="text-center py-5">
                    <FaSearch size={48} className="text-muted mb-3" />
                    <h4>No gallery items found</h4>
                    <p className="text-muted">
                      {searchTerm || selectedCategory !== 'all' || selectedStatus !== 'all'
                        ? 'Try changing your search or filters'
                        : 'Add your first gallery item to get started'}
                    </p>
                    {searchTerm || selectedCategory !== 'all' || selectedStatus !== 'all' ? (
                      <Button variant="outline-primary" onClick={resetFilters}>
                        Reset Filters
                      </Button>
                    ) : (
                      <Button variant="primary" onClick={handleCreate}>
                        <FaPlus className="me-2" /> Add Photo
                      </Button>
                    )}
                  </Col>
                )}
              </Row>

              {/* Pagination for Grid View */}
              {totalPages > 1 && (
                <div className="p-4 border-top">
                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <div className="text-muted">
                      Showing page {currentPage} of {totalPages}
                      {totalItems > 0 && ` (Total: ${totalItems} items)`}
                    </div>
                    <Pagination className="mb-0">
                      {renderPagination()}
                    </Pagination>
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="table-responsive">
                <Table hover className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: '80px' }}>#</th>
                      <th>Photo</th>
                      <th style={{ cursor: 'pointer' }} onClick={() => requestSort('event')}>
                        Event {sortConfig.key === 'event' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                      </th>
                      <th style={{ cursor: 'pointer' }} onClick={() => requestSort('category')}>
                        Category {sortConfig.key === 'category' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                      </th>
                      <th>Status</th>
                      <th style={{ cursor: 'pointer' }} onClick={() => requestSort('upload_on')}>
                        Upload Date {sortConfig.key === 'upload_on' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                      </th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItems.length > 0 ? (
                      filteredItems.map((item, index) => (
                        <tr key={item.id}>
                          <td>{index + 1}</td>
                          <td>
                            <Image
                              src={getImageUrl(item)}
                              alt={item.event}
                              thumbnail
                              style={{ width: '80px', height: '60px', objectFit: 'cover' }}
                              onError={(e) => {
                                e.target.src = 'https://via.placeholder.com/80x60?text=No+Image';
                              }}
                            />
                          </td>
                          <td>
                            <div className="fw-semibold">{item.event}</div>
                          </td>
                          <td>
                            <Badge bg="info">{item.category}</Badge>
                          </td>
                          <td>
                            <Badge bg={item.archive === 1 ? "secondary" : "success"}>
                              {item.archive === 1 ? 'Archived' : 'Active'}
                            </Badge>
                          </td>
                          <td>
                            <small className="text-muted">
                              {new Date(item.upload_on).toLocaleDateString()}
                            </small>
                          </td>
                          <td>
                            <div className="d-flex gap-2">
                              <Button
                                variant="outline-primary"
                                onClick={() => handleEdit(item)}
                                size="sm"
                                disabled={deletingId !== null}
                                title="Edit"
                              >
                                <FaEdit />
                              </Button>
                              <Button
                                variant="outline-danger"
                                onClick={() => handleDelete(item.id)}
                                size="sm"
                                disabled={deletingId !== null}
                                title="Delete"
                              >
                                {deletingId === item.id ? (
                                  <Spinner animation="border" size="sm" />
                                ) : (
                                  <FaTrash />
                                )}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" className="text-center py-5">
                          <FaSearch size={48} className="text-muted mb-3" />
                          <h4>No gallery items found</h4>
                          <p className="text-muted">
                            {searchTerm || selectedCategory !== 'all' || selectedStatus !== 'all'
                              ? 'Try changing your search or filters'
                              : 'Add your first gallery item to get started'}
                          </p>
                          {searchTerm || selectedCategory !== 'all' || selectedStatus !== 'all' ? (
                            <Button variant="outline-primary" onClick={resetFilters}>
                              Reset Filters
                            </Button>
                          ) : (
                            <Button variant="primary" onClick={handleCreate}>
                              <FaPlus className="me-2" /> Add Photo
                            </Button>
                          )}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>

              {/* Pagination for Table View */}
              {totalPages > 1 && (
                <div className="p-3 border-top">
                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <div className="text-muted">
                      Showing page {currentPage} of {totalPages}
                      {totalItems > 0 && ` (Total: ${totalItems} items)`}
                    </div>
                    <Pagination className="mb-0">
                      {renderPagination()}
                    </Pagination>
                  </div>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* Gallery Item Modal */}
      <Modal show={showModal} onHide={() => !isSubmitting && setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{currentItem.id ? 'Edit Gallery Item' : 'Add Gallery Item'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Event Name *</Form.Label>
              <Form.Control
                type="text"
                name="event"
                value={currentItem.event}
                onChange={handleChange}
                isInvalid={!!formErrors.event}
                required
                disabled={isSubmitting}
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.event}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Category *</Form.Label>
              <Form.Control
                type="text"
                name="category"
                value={currentItem.category}
                onChange={handleChange}
                isInvalid={!!formErrors.category}
                required
                disabled={isSubmitting}
                placeholder="Enter category (e.g., University, Event, Conference)"
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.category}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                {currentItem.id ? 'Update Photo (Optional)' : 'Photo *'}
              </Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                isInvalid={!!formErrors.photo}
                required={!currentItem.id}
                disabled={isSubmitting}
              />
              <Form.Text className="text-muted">
                Allowed formats: JPG, PNG, GIF, WEBP, SVG. Max size: 5MB
              </Form.Text>
              <Form.Control.Feedback type="invalid">
                {formErrors.photo}
              </Form.Control.Feedback>
              {previewImage && (
                <div className="mt-3">
                  <Image
                    src={previewImage}
                    alt="Preview"
                    thumbnail
                    style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'contain' }}
                  />
                </div>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Archive this item"
                name="archive"
                checked={currentItem.archive}
                onChange={handleChange}
                disabled={isSubmitting}
              />
              <Form.Text className="text-muted">
                Archived items will not be displayed in the public gallery
              </Form.Text>
            </Form.Group>

            <div className="d-flex justify-content-end mt-3">
              <Button
                variant="secondary"
                onClick={() => setShowModal(false)}
                className="me-2"
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    {currentItem.id ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  currentItem.id ? 'Update Item' : 'Create Item'
                )}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default Gallery;